<script setup>
import { computed, ref, watch } from 'vue';
import CaptainResponseAPI from 'dashboard/api/captain/response';
import ResponseFiltersBar from 'dashboard/components-next/captain/assistant/ResponseFiltersBar.vue';

const props = defineProps({
  assistantId: {
    type: [String, Number],
    default: null,
  },
});

const emit = defineEmits(['change']);

const source = ref('all');
const status = ref(null);
const responsibleId = ref(null);
const responsibles = ref([]);
const isFetchingResponsibles = ref(false);

const hasActiveFilters = computed(
  () =>
    source.value !== 'all' ||
    Boolean(status.value) ||
    Boolean(responsibleId.value)
);

const buildParams = (page = 1) => {
  const params = { page };
  if (source.value !== 'all') params.createdVia = source.value;
  if (status.value) params.status = status.value;
  if (responsibleId.value) params.responsibleId = responsibleId.value;
  return params;
};

const emitChange = () => emit('change');

const handleSourceSelect = sourceKey => {
  source.value = sourceKey;
  emitChange();
};

const handleStatusSelect = statusKey => {
  status.value = statusKey;
  emitChange();
};

const handleResponsibleSelect = userId => {
  responsibleId.value = userId;
  emitChange();
};

const fetchResponsibles = async () => {
  isFetchingResponsibles.value = true;
  try {
    const response = await CaptainResponseAPI.responsibles({
      assistantId: props.assistantId,
    });
    responsibles.value = response.data.payload;
  } finally {
    isFetchingResponsibles.value = false;
  }
};

watch(() => props.assistantId, fetchResponsibles, { immediate: true });

const reset = () => {
  source.value = 'all';
  status.value = null;
  responsibleId.value = null;
};

defineExpose({ buildParams, reset, hasActiveFilters });
</script>

<template>
  <ResponseFiltersBar
    :active-source-filter="source"
    :active-status-filter="status"
    :active-responsible-filter="responsibleId"
    :responsibles="responsibles"
    :is-fetching-responsibles="isFetchingResponsibles"
    @select-source="handleSourceSelect"
    @select-status="handleStatusSelect"
    @select-responsible="handleResponsibleSelect"
  />
</template>
